/**
 * Reusable Admin CRUD component.
 * Props:
 *   title        - page title
 *   apiPath      - e.g. '/api/admin/services'
 *   columns      - [{ key, label, render? }]
 *   FormComponent - the form component for create/edit
 *   idKey        - defaults to '_id'
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';

const AdminCRUD = ({ title, apiPath, columns, FormComponent, idKey = '_id' }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(apiPath);
      setItems(res.data.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, [apiPath]);

  useEffect(() => { load(); }, [load]);

  const flash = (msg, isError = false) => {
    if (isError) { setError(msg); setSuccess(''); }
    else { setSuccess(msg); setError(''); }
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Delete this item? This cannot be undone.')) return;
    try {
      await axios.delete(`${apiPath}/${item[idKey]}`);
      flash('Deleted successfully.');
      load();
    } catch (err) {
      flash(err.response?.data?.error || 'Delete failed.', true);
    }
  };

  const handleSave = async (data) => {
    try {
      if (editing) {
        await axios.put(`${apiPath}/${editing[idKey]}`, data);
        flash('Updated successfully.');
      } else {
        await axios.post(apiPath, data);
        flash('Created successfully.');
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (err) {
      flash(err.response?.data?.error || 'Save failed.', true);
    }
  };

  const handleEdit = (item) => { setEditing(item); setShowForm(true); };
  const handleNew = () => { setEditing(null); setShowForm(true); };
  const handleCancel = () => { setEditing(null); setShowForm(false); };

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#0f172a', margin: 0 }}>{title}</h1>
          {!showForm && (
            <button onClick={handleNew} style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', padding: '9px 18px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
              + Add New
            </button>
          )}
        </div>

        {/* Alerts */}
        {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', color: '#ef4444', fontSize: '13px' }}>{error}</div>}
        {success && <div style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', color: '#16a34a', fontSize: '13px' }}>{success}</div>}

        {/* Form */}
        {showForm && (
          <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '24px', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '15px', fontWeight: 600, color: '#0f172a', marginTop: 0 }}>
              {editing ? 'Edit Item' : 'New Item'}
            </h2>
            <FormComponent
              initialData={editing}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading…</div>
        ) : (
          <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  {columns.map(col => (
                    <th key={col.key} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>{col.label}</th>
                  ))}
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={columns.length + 1} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>No items yet.</td></tr>
                )}
                {items.map((item, idx) => (
                  <tr key={item[idKey] || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    {columns.map(col => (
                      <td key={col.key} style={{ padding: '10px 16px', color: '#334155', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {col.render ? col.render(item) : String(item[col.key] ?? '')}
                      </td>
                    ))}
                    <td style={{ padding: '10px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button onClick={() => handleEdit(item)} style={{ background: '#f0f9ff', color: '#3b82f6', border: 'none', borderRadius: '4px', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', marginRight: '6px' }}>Edit</button>
                      <button onClick={() => handleDelete(item)} style={{ background: '#fef2f2', color: '#ef4444', border: 'none', borderRadius: '4px', padding: '5px 12px', fontSize: '12px', cursor: 'pointer' }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCRUD;
